import { createApi } from '@reduxjs/toolkit/query/react';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { API_BASE_URL } from '../config';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
    'Accept-Language': 'en-US,en;q=0.9',
  },
});

// Axios interceptor for adding auth token
axiosInstance.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios interceptor for handling 401 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      localStorage.clear();
      sessionStorage.clear();
      // Redirect handled by components
    }
    return Promise.reject(error);
  }
);

// Custom baseQuery using axios
const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string }) =>
  async ({ url, method = 'GET', data, params, headers }: any) => {
    try {
      const config: AxiosRequestConfig = {
        url: url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`,
        method,
        data,
        params,
        headers,
      };

      const result = await axiosInstance(config);
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError<{ message?: string; error?: string; details?: string }>;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
          message: err.response?.data?.message || err.response?.data?.error || err.response?.data?.details || err.message,
        },
      };
    }
  };

// Create the API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery({ baseUrl: API_BASE_URL }),
  tagTypes: ['WhoAmI', 'Profile', 'Rating', 'SignedUrl', 'LinkedIn', 'Referral'],
  endpoints: (builder) => ({
    // WhoAmI endpoint
    getWhoAmI: builder.query<any, { referralCode?: string }>({
      query: ({ referralCode }) => ({
        url: referralCode
          ? `/protected/whoami?referral_code=${encodeURIComponent(referralCode)}`
          : '/protected/whoami',
        method: 'GET',
      }),
      providesTags: ['WhoAmI'],
    }),

    // Profile data endpoint
    getProfileData: builder.query<any, void>({
      query: () => ({
        url: '/protected/profiling/data',
        method: 'GET',
      }),
      providesTags: ['Profile'],
    }),

    // Relevance AI profile data
    getRelevanceAiProfileData: builder.query<any, { linkedinUrl: string }>({
      query: ({ linkedinUrl }) => ({
        url: `/relivance-ai/profile-data?token=r5Yy1UYK3ePTFy1T3pf1qTDTXP81mmbykIHI45wiFrrXo8P1Ogu3C2osfH04SPCR&linkedin_url=${encodeURIComponent(linkedinUrl)}`,
        method: 'GET',
      }),
      providesTags: ['Profile'],
    }),

    // Rating options
    getRatingOptions: builder.query<any, void>({
      query: () => ({
        url: '/protected/rating/options',
        method: 'GET',
      }),
      providesTags: ['Rating'],
    }),

    // Get call rating
    getCallRating: builder.query<any, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/protected/rating?conversation_id=${conversationId}`,
        method: 'GET',
      }),
      providesTags: ['Rating'],
    }),

    // Check rating status
    checkRatingStatus: builder.query<any, void>({
      query: () => ({
        url: '/protected/rating/check-status',
        method: 'GET',
      }),
      providesTags: ['Rating'],
    }),

    // Submit rating
    submitRating: builder.mutation<any, any>({
      query: (data) => ({
        url: '/protected/rating',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Rating'],
    }),

    // Get signed URL (ElevenLabs)
    getSignedUrl: builder.query<any, { params?: { v?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_term?: string; utm_content?: string } }>({
      query: ({ params }) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value) queryParams.append(key, value);
          });
        }
        const queryString = queryParams.toString();
        return {
          url: `/protected/eleven_labs/signed-url${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['SignedUrl'],
    }),

    // Get user profile (LinkedIn info)
    getUserProfile: builder.query<any, { detailed?: boolean }>({
      query: ({ detailed = true }) => ({
        url: `/protected/user-profile?detailed=${detailed}`,
        method: 'GET',
      }),
      providesTags: ['LinkedIn'],
    }),

    // Get relevance AI profile data
    getRelevanceAiProfileDataByUrl: builder.query<any, void>({
      query: () => ({
        url: '/protected/relivance-ai/profile-data',
        method: 'GET',
      }),
      providesTags: ['Profile'],
    }),

    // Get LinkedIn info
    getLinkedInInfo: builder.query<any, { linkedinUrl: string }>({
      query: ({ linkedinUrl }) => ({
        url: `/api/linkedin`,
        method: 'POST',
        data: { linkedinUrl },
      }),
      providesTags: ['LinkedIn'],
    }),

    // Get referral link
    getReferralLink: builder.query<any, void>({
      query: () => ({
        url: '/protected/referral-link',
        method: 'GET',
      }),
      providesTags: ['Referral'],
    }),
  }),
});

export const {
  useGetWhoAmIQuery,
  useGetProfileDataQuery,
  useGetRelevanceAiProfileDataQuery,
  useGetRelevanceAiProfileDataByUrlQuery,
  useGetRatingOptionsQuery,
  useGetCallRatingQuery,
  useCheckRatingStatusQuery,
  useSubmitRatingMutation,
  useGetSignedUrlQuery,
  useGetLinkedInInfoQuery,
  useGetUserProfileQuery,
  useGetReferralLinkQuery,
} = apiSlice;
